import {Checkbox, Flag} from 'platform/components';
import {HStack} from 'platform/foundation';

import {useId} from 'react';

import {count} from 'ramda';

import {PriceMapCarResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {isAvailable} from '../utils/isAvailable';
import {isSoldIn30Days} from '../utils/isSoldIn30Days';
import {isSoldIn90Days} from '../utils/isSoldIn90Days';
import {isUnsupportedCombination} from '../utils/isUnsupportedCombination';

interface ChartCheckboxesProps extends TestIdProps {
  values: {
    onStockChecked: boolean;
    soldRecentlyChecked: boolean;
    soldChecked: boolean;
  };
  onChange: (values: {
    onStockChecked: boolean;
    soldRecentlyChecked: boolean;
    soldChecked: boolean;
  }) => void;
  cars: PriceMapCarResponseBody[];
}

export function ChartCheckboxes(props: ChartCheckboxesProps) {
  const stockCheckboxId = useId();
  const soldRecentlyCheckboxId = useId();
  const soldCheckboxId = useId();

  const onStockCount = count((car) => isAvailable(car), props.cars);
  const soldIn30DaysCount = count((car) => isSoldIn30Days(car), props.cars);
  const soldIn90DaysCount = count((car) => isSoldIn90Days(car), props.cars);

  const isShowStockCarsDisabled = isUnsupportedCombination(
    !props.values.onStockChecked,
    props.values.soldRecentlyChecked,
    props.values.soldChecked
  );
  const isShowSoldIn30Disabled = isUnsupportedCombination(
    props.values.onStockChecked,
    !props.values.soldRecentlyChecked,
    props.values.soldChecked
  );
  const isShowSoldIn90Disabled = isUnsupportedCombination(
    props.values.onStockChecked,
    props.values.soldRecentlyChecked,
    !props.values.soldChecked
  );

  return (
    <HStack spacing={4} justify="center">
      <HStack align="center" spacing={2}>
        <Checkbox
          externalLabelId={stockCheckboxId}
          value={props.values.onStockChecked}
          onChange={(value) => props.onChange({...props.values, onStockChecked: value})}
          isDisabled={isShowStockCarsDisabled}
          data-testid={suffixTestId('stockChecked', props)}
        />
        <label htmlFor={stockCheckboxId}>
          <Flag
            label={`${i18n.t('entity.priceMap.labels.onStock')} (${onStockCount})`}
            colorScheme="orange"
            data-testid={suffixTestId('stock', props)}
          />
        </label>
      </HStack>
      <HStack align="center" spacing={2}>
        <Checkbox
          externalLabelId={soldRecentlyCheckboxId}
          value={props.values.soldRecentlyChecked}
          onChange={(value) => props.onChange({...props.values, soldRecentlyChecked: value})}
          isDisabled={isShowSoldIn30Disabled}
          data-testid={suffixTestId('soldRecentlyChecked', props)}
        />
        <label htmlFor={soldRecentlyCheckboxId}>
          <Flag
            label={`${i18n.t('entity.priceMap.labels.soldIn30Days')} (${soldIn30DaysCount})`}
            colorScheme="neutral"
            data-testid={suffixTestId('soldRecently', props)}
          />
        </label>
      </HStack>
      <HStack align="center" spacing={2}>
        <Checkbox
          externalLabelId={soldCheckboxId}
          value={props.values.soldChecked}
          onChange={(value) => props.onChange({...props.values, soldChecked: value})}
          isDisabled={isShowSoldIn90Disabled}
          data-testid={suffixTestId('soldChecked', props)}
        />
        <label htmlFor={soldCheckboxId}>
          <Flag
            label={`${i18n.t('entity.priceMap.labels.soldIn90Days')} (${soldIn90DaysCount})`}
            colorScheme="black"
            isSubtle
            data-testid={suffixTestId('sold', props)}
          />
        </label>
      </HStack>
    </HStack>
  );
}
